
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// module components
import PretensionFiles from '@/modules/PretensionsModule/components/PretensionFiles/index.vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// validation
import useVuelidate from '@vuelidate/core'

// validation
import { required, requiredIf } from '@vuelidate/validators'

// types
import { AgreeComplaintParams, Complaint } from '@/new-services/types/complaints'
import { EmployeeT, UserDictionaryT } from '@/core/types/users.types'

// @new-services
import complaintsAPI from '@/new-services/complaints.service'

// realisations
import { users } from '@/core/realisations/users.realisation'

// constants
import {
    CLOSE_STATUS_ID,
    COMPLAINT_TYPES,
    COMPLAINT_TYPE_OPTIONS,
} from '@/core/constants/complaints.constants'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    props: {
        complaintId: {
            type: Number,
            default: null,
        },
    },
    components: {
        PageTitle,
        TheButton,
        TheSelect,
        TheInput,
        Cheap,
        TheTextarea,
        ButtonDropdown,
        Loader,
        PretensionFiles,
        FileReader,
    },
    emits: ['refresh'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            //
            complaint: null as Complaint | null,
        })

        //

        const payload_agree = reactive({
            culpritSideId: null as number | null,
            culpritEmployeeId: null as number | null,
            agreementDate: null as string | null,
            agreement: null as string | null,
            categoryId: null as number | null,
        })

        const ComplaintAgreeValidation = {
            culpritSideId: { required: requiredIf(() => payload_agree.categoryId === 66) },
            culpritEmployeeId: { required: requiredIf(() => payload_agree.categoryId === 66) },
            categoryId: { required },
            agreementDate: { required },
            agreement: { required },
        }

        const payload_agree_form$ = useVuelidate(ComplaintAgreeValidation, payload_agree)

        //

        const payload_add = reactive({
            typeId: 42,
            clientId: null as number | null,
            title: null as string | null,
            content: null as string | null,
            categoryId: null as number | null,
            files: [] as File[],
        })

        const ComplaintAddValidation = {
            clientId: { required },
            title: { required },
            content: { required },
            categoryId: { required },
        }

        const payload_add_form$ = useVuelidate(ComplaintAddValidation, payload_add)

        //

        const data_state = reactive({
            departments: new DataState<UserDictionaryT>({}),
            employees: new DataState<EmployeeT>({}),
        })

        const resetAgreeForm = () => {
            payload_agree.culpritSideId = null
            payload_agree.culpritEmployeeId = null
            payload_agree.categoryId = null
            payload_agree.agreementDate = null
            payload_agree.agreement = null

            payload_agree_form$.value.$reset()
        }

        const setComplaintInfo = async () => {
            if (!state.complaint) return

            payload_agree.culpritSideId = state.complaint.culpritSideId
            payload_agree.culpritEmployeeId = state.complaint.culpritEmployeeId
            payload_agree.categoryId = state.complaint.categoryId
            payload_agree.agreementDate = state.complaint.agreementDate
            payload_agree.agreement = state.complaint.agreement
        }

        const loadComplaint = async () => {
            try {
                state.is_loading = true

                const response = await complaintsAPI.getComplaint(props.complaintId)
                state.complaint = response.data.data

                setComplaintInfo()

                state.is_failed = false
            } catch (error) {
                console.error(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const loadDictionary = async (type: number) => {
            try {
                data_state.departments.is_loading = true

                const response = await users.getDictionary(type)
                data_state.departments.list = response.data

                data_state.departments.is_failed = false
                data_state.departments.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.departments.failure.catchError(error)

                if (!data_state.departments.failure.error_state.is_cancel) {
                    data_state.departments.is_failed = true
                }
            } finally {
                if (!data_state.departments.failure.error_state.is_cancel) {
                    data_state.departments.is_loading = false
                }
            }
        }

        //

        const loadEmployees = async (page: number, search?: string) => {
            data_state.employees.pagination.current_page = page

            try {
                data_state.employees.is_loading = true

                const params = {
                    page,
                    search,
                    departmentId: payload_agree.culpritSideId,
                    limit: 9999,
                }

                const response = await users.getEmployees(params)

                data_state.employees.list = response.data

                data_state.employees.pagination.total_pages = response.meta.last_page

                data_state.employees.is_failed = false
                data_state.employees.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.employees.failure.catchError(error)

                if (!data_state.employees.failure.error_state.is_cancel) {
                    data_state.employees.is_failed = true
                }
            } finally {
                if (!data_state.employees.failure.error_state.is_cancel) {
                    data_state.employees.is_loading = false
                }
            }
        }

        const loadMoreEmployees = async (page: number) => {
            if (page > data_state.employees.pagination.total_pages) return
            data_state.employees.pagination.current_page = page

            try {
                data_state.employees.is_loading = true

                const response = await users.getEmployees({
                    page,
                    limit: 9999,
                })
                data_state.employees.list = data_state.employees.list.concat(response.data)

                data_state.employees.pagination.total_pages = response.meta.last_page

                data_state.employees.is_failed = false
                data_state.employees.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.employees.failure.catchError(error)

                if (!data_state.employees.failure.error_state.is_cancel) {
                    data_state.employees.is_failed = true
                }
            } finally {
                if (!data_state.employees.failure.error_state.is_cancel) {
                    data_state.employees.is_loading = false
                }
            }
        }

        const searchEmployees = (search_query: string) => {
            loadEmployees(1, search_query)
        }

        const unshiftFindedEmployee = async (employee: EmployeeT) => {
            await loadEmployees(data_state.employees.pagination.current_page)
            data_state.employees.list.unshift(employee)
        }

        //

        const agreeComplaint = async () => {
            payload_agree_form$.value.$touch()
            if (payload_agree_form$.value.$invalid) return

            try {
                state.is_loading = true

                const params = { ...payload_agree } as AgreeComplaintParams

                await complaintsAPI.agreeComplaint(props.complaintId, params)

                notifications.notify({ title: 'Жалоба согласована!', type: 'success' })
                await loadComplaint()
                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка согласования`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const addComplaint = async () => {
            payload_add_form$.value.$touch()
            if (payload_add_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload = {
                    ...payload_add,
                    clientType: 'Сотрудники',
                    files: payload_add.files.length ? payload_add.files : null,
                    source: 'BPMS',
                }
                await complaintsAPI.addComplaint(getFormData(payload))

                notifications.notify({ title: 'Жалоба создана!', type: 'success' })
                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка создания`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const closeComplaint = async () => {
            try {
                state.is_loading = true

                await complaintsAPI.changeComplaintStatus(props.complaintId, CLOSE_STATUS_ID)

                notifications.notify({ title: 'Жалоба закрыта!', type: 'success' })
                await loadComplaint()
                emit('refresh')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка при закрытии жалобы!`,
                })
            } finally {
                state.is_loading = false
            }
        }

        //

        const emitCancel = () => {
            emit('refresh')
        }

        // computed

        const isType41 = computed(
            () => state.complaint && state.complaint.type && state.complaint.type.id === 41
        )
        const isType42 = computed(
            () => state.complaint && state.complaint.type && state.complaint.type.id === 42
        )
        const title = computed(() => (props.complaintId ? 'Согласование' : 'Создание жалобы'))

        //

        onBeforeMount(() => {
            if (props.complaintId) {
                loadComplaint()
            }

            loadDictionary(1)
            loadEmployees(1)
        })

        //

        return {
            state,
            data_state,
            payload_agree,
            payload_add,
            //
            payload_agree_form$,
            payload_add_form$,
            //
            loadComplaint,
            resetAgreeForm,
            agreeComplaint,
            addComplaint,
            closeComplaint,
            //
            loadEmployees,
            loadMoreEmployees,
            searchEmployees,
            unshiftFindedEmployee,
            //
            emitCancel,
            //
            isType41,
            isType42,
            title,
            //
            COMPLAINT_TYPES,
            COMPLAINT_TYPE_OPTIONS,
        }
    },
})
